import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Modal, ModalBody, ModalFooter, Button, Input } from "reactstrap";
import i18n from "../../../i18n";
import { postFinishServices } from "../../../helpers/api_rombo_helper";
import { NOTE_MAX_LENGTH } from "../../../constants/enums";

const CloseServiceModal = ({ isOpen, toggle, selectedService, setShowAlertSuccess, setTextHeader, setTextBody }) => {

  const [reason, setReason] = useState("");
  const [isReasonValid, setIsReasonValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isShaking, setIsShaking] = useState(false);

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({
      token: Login.token,
      data: Login.data,
    })
  );

  const { token, data } = useSelector(LoginProperties);

  const userProfile = {
    username: data?.data?.full_name || "",
    sub: data?.data?.id || ""
  };

  const handleCloseService = async () => {
    if (!reason.trim()) return;

    setIsLoading(true);

    try {
      const serviceCode = selectedService.code;

      const payload = {
        user_name: userProfile.username || userProfile.sub,
        reason
      };

      await postFinishServices(token, serviceCode, payload);

      setReason("");
      toggle();
      setShowAlertSuccess(true);
      setTextHeader(i18n.t("ServiceFinishedRemotely"));
      setTextBody(i18n.t("ServiceFinishedRemotelyBody"));
    } catch (error) {
      setHasError(true);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 1000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      role="dialog"
      autoFocus={false}
      centered
      className={`RequestModal ${isShaking ? "modal-error" : ""}`}
      toggle={toggle}
      backdrop="static"
      size="lg"
    >
      <div>
        <div className="modal-header-container">
          <p className="cancellation-title modal-header-bottom">
            {i18n.t("CloseServiceRemotely")}
          </p>
          <p className="cancellation-text modal-service-id">
            {selectedService?.code}
          </p>
        </div>
        <div className="modal-separator" />
        <ModalBody>
          <p className="modal-text-bold">
            {i18n.t("CloseServiceText")}
          </p>
          {hasError && (
            <div className="alert alert-danger mb-3" role="alert">
              {i18n.t("CloseServiceError")}
            </div>
          )}
          <Input
            type="textarea"
            className={`form-control ${hasError ? "is-invalid" : ""}`}
            placeholder={i18n.t("WriteYourNoteHere")}
            value={reason}
            maxLength={NOTE_MAX_LENGTH}
            onChange={(e) => {
              const value = e.target.value.slice(0, NOTE_MAX_LENGTH);
              setReason(value);
              setIsReasonValid(value.trim() !== "");

              if (hasError) setHasError(false);
            }}
          />
          <small className="text-muted">
            {reason.length}/{NOTE_MAX_LENGTH} {i18n.t("Characters")}
          </small>
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn btn-users border"
            type="button"
            onClick={handleCloseService}
            disabled={!isReasonValid || isLoading}
          >
            {isLoading ? i18n.t("Finishing...") : i18n.t("Accept")}
          </Button>
          <Button
            className="btn btn-light"
            type="button"
            onClick={toggle}
          >
            {i18n.t("Cancel")}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

CloseServiceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  selectedService: PropTypes.object.isRequired,
  setShowAlertSuccess: PropTypes.func.isRequired,
  setTextHeader: PropTypes.func.isRequired,
  setTextBody: PropTypes.func.isRequired
};

export default CloseServiceModal;
